// File: src/components/Admin/PaymentPosting.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  alpha,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface Customer {
  CustID: number;
  CompanyName: string;
  FirstName: string;
  LastName: string;
}

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  total_amount: number;
  balance_due: number;
  status: string;
  aging_status: 'overdue' | 'due_soon' | 'current';
}

interface PaymentMethod {
  payment_method: string;
  fee_type: string;
  percentage_fee: number;
  fixed_fee: number;
  notes: string;
}

interface InvoiceAllocation {
  invoice_id: number;
  invoice_number: string;
  amount: number;
}

const PaymentPosting: React.FC = () => {
  const theme = useTheme();
  const { member } = useAuth();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customerBalance, setCustomerBalance] = useState({
    credit_balance: 0,
    total_outstanding: 0,
    net_balance: 0
  });
  const [customerBillingType, setCustomerBillingType] = useState<'prepaid' | 'postpaid' | null>(null);
  
  // Form state
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: '',
    reference_number: '',
    notes: '',
    allocation_type: 'oldest_first',
    invoice_allocations: [] as InvoiceAllocation[]
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Check if user is authorized
  const isAuthorized = member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin';

  useEffect(() => {
    if (isAuthorized) {
      loadPaymentOptions();
      loadCustomers();
    }
  }, [isAuthorized]);

  useEffect(() => {
    if (selectedCustomer) {
      loadCustomerData();
    }
  }, [selectedCustomer]);

  const loadPaymentOptions = async () => {
    try {
      const response = await api.get('/endpoints/payment-posting?action=options');
      console.log('Payment options response:', response.data);
      if (response.data.success) {
        setPaymentMethods(response.data.data.payment_methods);
        console.log('Payment methods loaded:', response.data.data.payment_methods);
      }
    } catch (error) {
      console.error('Error loading payment options:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/endpoints/customers');
      if (response.data) {
        // The customers endpoint returns data directly, not wrapped in a data property
        const customersData = Array.isArray(response.data) ? response.data : response.data.data || [];
        console.log('Raw customers data:', customersData);
        // Map the customer data to match our interface
        const mappedCustomers = customersData.map((c: any) => ({
          CustID: c.id || c.CustID,
          CompanyName: c.company || c.CompanyName,
          FirstName: c.firstName || c.FirstName,
          LastName: c.lastName || c.LastName
        }));
        console.log('Mapped customers:', mappedCustomers);
        setCustomers(mappedCustomers);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadCustomerData = async () => {
    if (!selectedCustomer) return;
    
    setLoading(true);
    try {
      // Load invoices
      const invoicesResponse = await api.get(
        `/endpoints/payment-posting?action=pending-invoices&custid=${selectedCustomer}`
      );
      if (invoicesResponse.data.success) {
        setInvoices(invoicesResponse.data.data.invoices);
        setCustomerBillingType(invoicesResponse.data.data.billing_type || null);
      }
      
      // Load balance
      const balanceResponse = await api.get(
        `/endpoints/payment-posting?action=customer-balance&custid=${selectedCustomer}`
      );
      if (balanceResponse.data.success) {
        setCustomerBalance(balanceResponse.data.data);
      }
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    console.log('Validating form:', {
      selectedCustomer,
      amount: paymentData.amount,
      payment_method: paymentData.payment_method
    });
    
    if (!selectedCustomer) {
      newErrors.customer = 'Please select a customer';
    }
    
    const amount = parseFloat(paymentData.amount);
    if (!paymentData.amount || isNaN(amount) || amount <= 0) {
      newErrors.amount = 'Please enter a valid amount greater than 0';
    }
    
    if (!paymentData.payment_method) {
      newErrors.payment_method = 'Please select a payment method';
    }
    
    // Validate allocations if specific invoices selected
    if (paymentData.allocation_type === 'specific_invoices') {
      const totalAllocated = paymentData.invoice_allocations.reduce(
        (sum, alloc) => sum + alloc.amount, 0
      );
      if (totalAllocated > amount) {
        newErrors.allocations = 'Total allocated amount exceeds payment amount';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted');
    
    if (!validateForm()) {
      console.log('Validation failed');
      return;
    }
    
    setLoading(true);
    setErrors({});
    setSuccessMessage('');
    
    console.log('Submitting payment:', {
      custid: selectedCustomer,
      amount: parseFloat(paymentData.amount),
      payment_method: paymentData.payment_method,
      allocation_type: paymentData.allocation_type
    });
    
    try {
      const response = await api.post('/endpoints/payment-posting', {
        custid: selectedCustomer,
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        reference_number: paymentData.reference_number,
        notes: paymentData.notes,
        allocation_type: paymentData.allocation_type,
        invoice_allocations: paymentData.allocation_type === 'specific_invoices' 
          ? paymentData.invoice_allocations 
          : undefined
      });
      
      if (response.data.success) {
        setSuccessMessage(
          `Payment of $${paymentData.amount} posted successfully. ` +
          `Transaction ID: ${response.data.data.transaction_id}`
        );
        setShowSuccess(true);
        
        // Reset form
        setPaymentData({
          amount: '',
          payment_method: '',
          reference_number: '',
          notes: '',
          allocation_type: 'oldest_first',
          invoice_allocations: []
        });
        
        // Reload customer data
        loadCustomerData();
      }
    } catch (error: any) {
      console.error('Error posting payment:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      setErrors({ submit: error.response?.data?.message || error.message || 'Failed to post payment' });
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceAllocation = (invoiceId: number, amount: string) => {
    const numAmount = parseFloat(amount) || 0;
    const invoice = invoices.find(inv => inv.id === invoiceId);
    
    if (!invoice) return;
    
    setPaymentData(prev => ({
      ...prev,
      invoice_allocations: prev.invoice_allocations.map(alloc =>
        alloc.invoice_id === invoiceId
          ? { ...alloc, amount: numAmount }
          : alloc
      ).concat(
        prev.invoice_allocations.find(a => a.invoice_id === invoiceId)
          ? []
          : [{ invoice_id: invoiceId, invoice_number: invoice.invoice_number, amount: numAmount }]
      ).filter(alloc => alloc.amount > 0)
    }));
  };

  const getAllocationAmount = (invoiceId: number): string => {
    const allocation = paymentData.invoice_allocations.find(a => a.invoice_id === invoiceId);
    return allocation ? allocation.amount.toString() : '';
  };

  const formatPaymentMethod = (method: string): string => {
    return method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card':
      case 'debit_card':
        return <CreditCardIcon />;
      case 'etransfer':
      case 'bank_transfer':
      case 'direct_deposit':
        return <AccountBalanceIcon />;
      default:
        return <WalletIcon />;
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You are not authorized to access this page. Only administrators can post payments.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        Post Payment
      </Typography>
      
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      {errors.submit && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.submit}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CardHeader 
              title="Payment Details"
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${theme.palette.divider}`
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Customer"
                      value={selectedCustomer || ''}
                      onChange={(e) => setSelectedCustomer(Number(e.target.value))}
                      error={!!errors.customer}
                      helperText={errors.customer}
                      disabled={loading}
                      required
                    >
                      <MenuItem value="">
                        <em>Select a customer...</em>
                      </MenuItem>
                      {customers.map(customer => (
                        <MenuItem key={customer.CustID} value={customer.CustID}>
                          {customer.CompanyName || `${customer.FirstName} ${customer.LastName}`} 
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (ID: {customer.CustID})
                          </Typography>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Payment Amount"
                      type="number"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                      error={!!errors.amount}
                      helperText={errors.amount}
                      disabled={loading}
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: 0.01 }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Payment Method"
                      value={paymentData.payment_method}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, payment_method: e.target.value }))}
                      error={!!errors.payment_method}
                      helperText={errors.payment_method}
                      disabled={loading}
                      required
                    >
                      <MenuItem value="">
                        <em>Select payment method...</em>
                      </MenuItem>
                      {paymentMethods.map(method => (
                        <MenuItem key={method.payment_method} value={method.payment_method}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            {getPaymentMethodIcon(method.payment_method)}
                            <Typography sx={{ ml: 1 }}>
                              {formatPaymentMethod(method.payment_method)}
                            </Typography>
                            {method.notes && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                ({method.notes})
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reference Number"
                      value={paymentData.reference_number}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
                      placeholder="Cheque #, e-Transfer ref, etc."
                      disabled={loading}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={2}
                      value={paymentData.notes}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                      disabled={loading}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                        Payment Allocation
                      </FormLabel>
                      <RadioGroup
                        value={paymentData.allocation_type}
                        onChange={(e) => setPaymentData(prev => ({ ...prev, allocation_type: e.target.value }))}
                      >
                        {customerBillingType === 'postpaid' && (
                          <FormControlLabel
                            value="oldest_first"
                            control={<Radio />}
                            label="Apply to oldest invoices first (recommended)"
                            disabled={loading}
                          />
                        )}
                        {customerBillingType === 'postpaid' && (
                          <FormControlLabel
                            value="specific_invoices"
                            control={<Radio />}
                            label="Apply to specific invoices"
                            disabled={loading || invoices.length === 0}
                          />
                        )}
                        <FormControlLabel
                          value="account_credit"
                          control={<Radio />}
                          label="Add to account credit"
                          disabled={loading}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  
                  {paymentData.allocation_type === 'specific_invoices' && invoices.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Invoice Allocations
                      </Typography>
                      {errors.allocations && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {errors.allocations}
                        </Alert>
                      )}
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Invoice #</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell align="right">Balance Due</TableCell>
                              <TableCell align="right">Amount to Apply</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {invoices.map(invoice => (
                              <TableRow key={invoice.id}>
                                <TableCell>
                                  {invoice.invoice_number}
                                  {invoice.aging_status === 'overdue' && (
                                    <Chip 
                                      label="Overdue" 
                                      size="small" 
                                      color="error" 
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>{new Date(invoice.due_date).toLocaleDateString()}</TableCell>
                                <TableCell align="right">${invoice.balance_due.toFixed(2)}</TableCell>
                                <TableCell align="right">
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={getAllocationAmount(invoice.id)}
                                    onChange={(e) => handleInvoiceAllocation(invoice.id, e.target.value)}
                                    inputProps={{ 
                                      min: 0, 
                                      max: invoice.balance_due,
                                      step: 0.01
                                    }}
                                    disabled={loading}
                                    sx={{ width: 120 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )}
                </Grid>
                
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    disabled={loading}
                    size="large"
                  >
                    Post Payment
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setPaymentData({
                        amount: '',
                        payment_method: '',
                        reference_number: '',
                        notes: '',
                        allocation_type: 'oldest_first',
                        invoice_allocations: []
                      });
                      setErrors({});
                    }}
                    disabled={loading}
                    size="large"
                  >
                    Clear
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          {selectedCustomer && (
            <>
              <Card 
                elevation={0} 
                sx={{ 
                  mb: 3, 
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.info.main, 0.02)
                }}
              >
                <CardHeader 
                  avatar={<AccountBalanceIcon color="info" />}
                  title="Customer Balance"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <List disablePadding>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText primary="Account Credit" />
                      <ListItemSecondaryAction>
                        <Typography variant="h6" color="success.main">
                          ${customerBalance.credit_balance.toFixed(2)}
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText primary="Outstanding Balance" />
                      <ListItemSecondaryAction>
                        <Typography variant="h6" color="error.main">
                          ${customerBalance.total_outstanding.toFixed(2)}
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider sx={{ my: 1 }} />
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText 
                        primary={<Typography variant="subtitle1" fontWeight={600}>Net Balance</Typography>} 
                      />
                      <ListItemSecondaryAction>
                        <Typography 
                          variant="h6" 
                          color={customerBalance.net_balance >= 0 ? 'success.main' : 'error.main'}
                          fontWeight={700}
                        >
                          ${customerBalance.net_balance.toFixed(2)}
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
              
              {customerBillingType === 'postpaid' && invoices.length > 0 && (
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                  <CardHeader 
                    avatar={<ReceiptIcon color="action" />}
                    title="Outstanding Invoices"
                    titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                  />
                  <CardContent>
                    <List disablePadding>
                      {invoices.map((invoice, index) => (
                        <React.Fragment key={invoice.id}>
                          <ListItem sx={{ px: 0, py: 1 }}>
                            <ListItemText 
                              primary={invoice.invoice_number}
                              secondary={`Due: ${new Date(invoice.due_date).toLocaleDateString()}`}
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography 
                                  variant="body1" 
                                  color={invoice.aging_status === 'overdue' ? 'error' : 'text.primary'}
                                  fontWeight={600}
                                >
                                  ${invoice.balance_due.toFixed(2)}
                                </Typography>
                                {invoice.aging_status === 'overdue' && (
                                  <Chip label="Overdue" size="small" color="error" />
                                )}
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < invoices.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentPosting;